import { CloudUpload } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { formatGanttTaskToTask, formatTaskToGanttTask } from '@blackhyve/utilities/gantt';
import CircularProgress from '@mui/material/CircularProgress';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bulkUploadDependency } from '../../../../../features/dependencies/store/dependenciesSlice';
import { uploadTasks } from '../../../../../features/tasks/store/tasksSlice';

import { addDays } from 'date-fns';
import { updateProjectKpi } from 'features/projects/store/projectsSlice';
import ganttStore from '../../ganttConfig/ganttStore';

const ImportMenu = ({ projectId, ganttId }) => {
  const dispatch = useDispatch();
  const gantt = ganttStore.getGantt(ganttId);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(undefined);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const isUpdated = useSelector((state) => state.tasks?.isUpdated);

  const handleImport = (event) => {
    if (file) {
      setLoading(true);
      const fileType = file?.name?.slice(file?.name?.lastIndexOf('.'));
      if (fileType === '.mpp' || fileType === '.xml') {
        gantt.importFromMSProject({
          server: 'https://gantt-project-export.azurewebsites.net/',
          data: file,
          durationUnit: 'day',
          taskProperties: [
            'ConstraintType',
            'ConstraintDate',
            'Baseline',
            'FreeSlack',
            'ActualStart',
            'ActualEnd',
          ],
          callback: (project) => onAfterImport(project),
        });
      } else if (fileType === '.xer') {
        gantt.importFromPrimaveraP6({
          server: 'https://gantt-project-export.azurewebsites.net/',
          data: file,
          durationUnit: 'day',
          taskProperties: [
            'ConstraintType',
            'ConstraintDate',
            'Baseline',
            'FreeSlack',
            'ActualStart',
            'ActualEnd',
          ],
          callback: (project) => onAfterImport(project),
        });
      } else {
        setLoading(false);
        console.log('Unknown file type', fileType);
      }
      setFile(undefined);
      setIsOpen(false);
    }
  };

  function onAfterImport(project) {
    if (project) {
      if (project.config.duration_unit) {
        gantt.config.duration_unit = project.config.duration_unit;
      }
      const formattedTask = project?.data?.data.map((task) => {
        task.imported_parent_id = task.parent;

        task.status = getTaskStatus(task.progress);

        task.work_days = parseFloat(task.duration).toFixed(0);

        task.end_date = addDays(new Date(task.$raw.Finish), 1);
        task.start_date = new Date(task.start_date);
        task.scheduled_end_date = new Date(task.end_date);

        task.forecasted_start_date = new Date(task.start_date);
        task.forecasted_end_date = new Date(task.end_date);

        if (task.status === 'active' || task.status === 'complete') {
          task.actual_start_date = new Date(
            task?.$custom_data?.ActualStart ? task.$custom_data.ActualStart : task.start_date
          );

          if (task.status === 'complete') {
            task.actual_end_date = new Date(
              task?.$custom_data?.ActualEnd ? task.$custom_data.ActualEnd : task.end_date
            );
            task.pr = 1;
            task.earned_planned_days = task.duration;
            task.earned_days = task.duration;
          }

          // const { productionRating, earnedDays, earnedPlannedDays, forecastedEndDate } =
          //   getProductionRatingFromPercentComplete({
          //     percentComplete: task.progress,
          //     startDate: task.actual_start_date,
          //     effectiveDate,
          //     scheduledDuration: task.duration,
          //   });
        }

        task.constraint_type = getConstraintType(task.$custom_data.ConstraintType) || 'asap';
        task.constraint_date = task.$custom_data.ConstraintDate
          ? new Date(task.$custom_data.ConstraintDate)
          : null;

        return formatGanttTaskToTask(task);
      });
      dispatch(uploadTasks({ project_id: projectId, tasks: formattedTask }))
        .then((response) => {
          const allTasks = response.payload?.entities?.task;
          const formattedTask = response.payload.apiData.map((task) => {
            return { ...formatTaskToGanttTask(task) };
          });

          if (project?.data?.links) {
            const allLinks = project.data.links.map((link) => {
              const linkType =
                link.type == '1' ? 'SS' : link.type == '2' ? 'FF' : link.type == '3' ? 'SF' : 'FS';
              return {
                source: allTasks[link.source].id,
                target: allTasks[link.target].id,
                type: linkType,
                lag: parseInt(link.lag),
              };
            });
            dispatch(bulkUploadDependency({ project_id: projectId, dependencies: allLinks }))
              .then((dependencyResponse) => {
                const tasks = {
                  data: formattedTask,
                  links: dependencyResponse.payload,
                };
                gantt.parse(tasks);
                gantt.autoSchedule();
                setLoading(false);
                setIsFileUpload(true);
              })
              .catch((error) => {
                console.error('Error while creating dependency =>', error);
                setLoading(false);
                setIsFileUpload(true);
              });
          } else {
            gantt.parse({
              data: formattedTask,
            });
            gantt.autoSchedule();
          }
        })
        .catch((error) => {
          console.error('Error while creating tasks =>', error);
          setLoading(false);
          setIsFileUpload(true);
        });
    } else {
      console.error('Error while uploading project');
      setLoading(false);
    }
  }
  useEffect(() => {
    if (isUpdated && isFileUpload) {
      dispatch(updateProjectKpi(projectId));
      setIsFileUpload(false);
    }
  }, [isUpdated, dispatch, projectId, isFileUpload]);

  return (
    <Fragment>
      {loading && <CircularProgress className="loading" />}
      <IconButton aria-label={'upload mpp or xer'} size={'small'} onClick={() => setIsOpen(true)}>
        <CloudUpload />
      </IconButton>
      <Dialog maxWidth="md" open={isOpen || loading} onClose={() => setIsOpen(false)}>
        <DialogTitle>Import Your Project</DialogTitle>
        <DialogContent style={loading ? { opacity: 0.3 } : {}}>
          <Typography>Upload MPP or XML file</Typography>
          <Box
            accept=".mpp, .xml, .xer"
            border={'1px dashed'}
            boxSizing={'borderBox'}
            component={'input'}
            padding={'5vw'}
            textAlign={'center'}
            type="file"
            onChange={(event) => setFile(event.target.files[0])}
          ></Box>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button disabled={!file} size={'small'} variant={'contained'} onClick={handleImport}>
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

const getTaskStatus = (progress) => {
  const parsedProgress = parseFloat(progress);
  if (parsedProgress === 1) {
    return 'complete';
  } else if (parsedProgress === 0) {
    return 'todo';
  } else {
    return 'active';
  }
};

const getConstraintType = (type) => {
  let constraintType = 'asap';
  switch (type) {
    case '1':
      constraintType = 'asap';
      break;
    case '2':
      constraintType = 'alap';
      break;
    case '3':
      constraintType = 'snlt';
      break;
    case '4':
      constraintType = 'snet';
      break;
    case '5':
      constraintType = 'fnet';
      break;
    case '6':
      constraintType = 'fnlt';
      break;
    case '7':
      constraintType = 'mso';
      break;
    case '8':
      constraintType = 'mfo';
      break;
    default:
      constraintType = 'asap';
      break;
  }
  return constraintType;
};

export default ImportMenu;
